import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, ArrowLeft, CheckCircle2, Dumbbell, Flame, Plus, Pencil, Trash2, CalendarDays, Volume2, VolumeX, Star, Music, Target, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { bell, beep, fmt } from "@/lib/ringaudio";
import { allLines, blockLine, coachPhrases, segmentLine, useCoachVoice } from "@/lib/coachVoice";
import { useMusic, TRACKS } from "@/lib/music";
import { WORKOUTS, KIND_META, customToWorkout, segmentsOf } from "@/data/workouts";
import ProgramBuilder from "@/pages/ProgramBuilder";
import WeeklyCalendar from "@/pages/WeeklyCalendar";

const authHeader = () => ({ headers: { "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" } });

const LEVEL_LABEL = {
  it: ["Personalizzato", "Intermedio 1", "Intermedio 2", "Intermedio 3", "Intermedio 4", "Intermedio 5"],
  en: ["Custom", "Intermediate 1", "Intermediate 2", "Intermediate 3", "Intermediate 4", "Intermediate 5"],
};

export default function WorkoutsTab({ initialProgram }) {
  const { lang } = useLang();
  const [view, setView] = useState("list");
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [customs, setCustoms] = useState(null);
  const pending = useRef(initialProgram);

  const load = () => api.get("/blueprint/programs", authHeader()).then((r) => setCustoms(r.data)).catch(() => setCustoms([]));
  useEffect(() => { load(); }, []);

  const customWorkouts = useMemo(() => (customs || []).map(customToWorkout), [customs]);
  const all = useMemo(() => [...WORKOUTS, ...customWorkouts], [customWorkouts]);

  useEffect(() => {
    if (!pending.current || customs === null) return;
    const w = all.find((x) => x.id === pending.current);
    pending.current = null;
    if (w) setActive(w);
  }, [customs, all]);

  const remove = async (w) => {
    if (!window.confirm(lang === "it" ? `Eliminare "${w.title.it}"?` : `Delete "${w.title.en}"?`)) return;
    await api.delete(`/blueprint/programs/${w.raw.id}`, authHeader());
    toast.success(lang === "it" ? "Programma eliminato" : "Program deleted");
    load();
  };

  if (active) return <WorkoutPlayer workout={active} onExit={() => setActive(null)} />;
  if (editing) return <ProgramBuilder initial={editing.raw} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  const subTabs = [
    { id: "list", label: lang === "it" ? "Programmi" : "Programs", icon: Dumbbell },
    { id: "calendar", label: lang === "it" ? "Calendario" : "Calendar", icon: CalendarDays },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {subTabs.map((s) => (
          <button key={s.id} onClick={() => setView(s.id)} data-testid={`workouts-view-${s.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${view === s.id ? "bg-white text-black border-white" : "border-[#272b35] text-zinc-400 hover:text-white"}`}>
            <s.icon size={13} /> {s.label}
          </button>
        ))}
      </div>
      {view === "calendar" ? (
        <WeeklyCalendar programs={all} onStart={setActive} />
      ) : (
        <ProgramList lang={lang} customs={customWorkouts} onStart={setActive} onEdit={setEditing} onDelete={remove} onCreate={() => setEditing({ raw: null })} />
      )}
    </div>
  );
}

function ProgramCard({ w, lang, onStart, onEdit, onDelete }) {
  return (
    <div data-testid={`workout-card-${w.id}`} className="relative bg-[#14161b] border border-[#272b35] hover:border-[#3a3f4b] rounded-xl p-5 transition-colors group">
      <button onClick={() => onStart(w)} className="absolute inset-0 rounded-xl" aria-label="start" data-testid={`workout-start-${w.id}`} />
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded text-white" style={{ backgroundColor: w.color }}>
          {LEVEL_LABEL[lang][w.level]}
        </span>
        {w.custom ? (
          <span className="relative z-10 flex gap-1">
            <button onClick={() => onEdit(w)} data-testid={`workout-edit-${w.id}`} className="p-1.5 text-zinc-500 hover:text-white"><Pencil size={14} /></button>
            <button onClick={() => onDelete(w)} data-testid={`workout-delete-${w.id}`} className="p-1.5 text-zinc-500 hover:text-[#E63946]"><Trash2 size={14} /></button>
          </span>
        ) : (
          <span className="flex items-center gap-1">{Array.from({ length: w.level }).map((_, i) => <Flame key={i} size={12} className="text-[#E63946]" />)}</span>
        )}
      </div>
      <h3 className="mt-3 font-heading text-2xl font-bold uppercase text-white">{w.title[lang]}</h3>
      <p className="mt-1 text-sm text-zinc-400">{w.focus[lang]}</p>
      <div className="mt-4 flex items-center gap-4 text-xs font-mono text-zinc-500">
        <span>{w.minutes} min</span>
        <span>{w.blocks.filter((b) => b.kind === "round").length} round</span>
        <span className="text-[#E63946] font-bold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
          <Play size={12} /> {lang === "it" ? "Avvia" : "Start"}
        </span>
      </div>
    </div>
  );
}

function ProgramList({ lang, customs, onStart, onEdit, onDelete, onCreate }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell size={18} className="text-[#E63946]" />
        <h2 className="font-heading text-2xl font-extrabold uppercase text-white">{lang === "it" ? "Programmi di Allenamento" : "Training Programs"}</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        {lang === "it" ? "5 allenamenti guidati di livello intermedio, a difficoltà crescente. Segui il player passo-passo." : "5 guided intermediate workouts, increasing difficulty. Follow the step-by-step player."}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {WORKOUTS.map((w) => <ProgramCard key={w.id} w={w} lang={lang} onStart={onStart} />)}
      </div>

      <div className="mt-10 flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Star size={18} className="text-[#E63946]" />
          <h2 className="font-heading text-2xl font-extrabold uppercase text-white">{lang === "it" ? "I miei programmi" : "My programs"}</h2>
        </div>
        <button onClick={onCreate} data-testid="create-program-btn" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E63946] hover:bg-[#D90429] text-white text-sm font-bold uppercase rounded-md">
          <Plus size={15} /> {lang === "it" ? "Crea" : "Build"}
        </button>
      </div>
      {customs.length ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {customs.map((w) => <ProgramCard key={w.id} w={w} lang={lang} onStart={onStart} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      ) : (
        <button onClick={onCreate} data-testid="create-program-empty" className="w-full rounded-xl border border-dashed border-[#3a3f4b] p-8 text-sm text-zinc-400 hover:text-white hover:border-[#E63946]">
          {lang === "it" ? "Nessun programma ancora. Costruisci il tuo allenamento su misura: round, combo, recuperi." : "No programs yet. Build your own workout: rounds, combos, rest."}
        </button>
      )}
    </div>
  );
}

function useTicker(running, onTick) {
  const cb = useRef(onTick);
  cb.current = onTick;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => cb.current(), 1000);
    return () => clearInterval(id);
  }, [running]);
}

function WorkoutPlayer({ workout, onExit }) {
  const { lang } = useLang();
  const blocks = workout.blocks;
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(blocks[0].seconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const announced = useRef(-1);
  const lastSeg = useRef(0);
  const lastTalk = useRef(0);
  const [style, setStyle] = useState(() => localStorage.getItem("guardia_coach_style") || "gritty");
  const [picked, setPicked] = useState(false);
  const P = coachPhrases(lang, style);
  const music = useMusic();
  const lines = useMemo(() => allLines(blocks, segmentsOf, lang, style), [blocks, lang, style]);
  const coach = useCoachVoice(picked ? lines : [], music.duck, style);
  useWakeLock(running);

  const block = blocks[idx];
  const meta = KIND_META[block.kind];
  const seg = segmentsOf(block);
  const elapsed = block.seconds - remaining;
  const segIdx = Math.min(seg.count - 1, Math.floor(elapsed / seg.segLen));

  const talk = (text) => { lastTalk.current = Date.now(); coach.say(text); };

  const goTo = (newIdx, autostart) => {
    if (newIdx < 0) return;
    lastSeg.current = 0;
    if (newIdx >= blocks.length) {
      setRunning(false);
      setDone(true);
      music.pause();
      bell();
      talk(P.done);
      return;
    }
    setIdx(newIdx);
    setRemaining(blocks[newIdx].seconds);
    if (autostart) setRunning(true);
  };

  useEffect(() => {
    if (running && announced.current !== idx) {
      announced.current = idx;
      setTimeout(() => talk(blockLine(blocks[idx], lang, style)), 700);
    }
  }, [running, idx]); // eslint-disable-line

  useEffect(() => () => { coach.stop(); music.pause(); }, []); // eslint-disable-line

  useTicker(running, () => {
    const r = remaining;
    if (r <= 1) { bell(); goTo(idx + 1, true); return; }
    const nr = r - 1;
    setRemaining(nr);
    const b = blocks[idx];
    const active = b.kind !== "rest" && b.kind !== "cooldown";
    const s = segmentsOf(b);
    const si = Math.min(s.count - 1, Math.floor((b.seconds - nr) / s.segLen));
    if (nr <= 3) return coach.enabled ? talk(P.count[nr - 1]) : beep(720, 0.12, "sine");
    if (si !== lastSeg.current) { lastSeg.current = si; if (s.count > 1) return talk(segmentLine(b, si, lang, style)); }
    if (b.kind === "rest" && nr === 10) return talk(P.restEnd);
    if (active && nr === 30 && b.seconds >= 60) return talk(P.last30);
    if (active && nr === 10) return talk(P.ten);
    if (active && nr > 33 && !coach.speaking.current && Date.now() - lastTalk.current > 14000 && Math.random() < 0.3) {
      talk(P.hype[Math.floor(Math.random() * P.hype.length)]);
    }
  });

  const start = () => {
    if (!running) { bell(); if (localStorage.getItem("guardia_music_on") !== "off") music.play(); }
    else coach.stop();
    setRunning(!running);
  };

  const toggleMusic = () => {
    if (music.playing) { music.pause(); localStorage.setItem("guardia_music_on", "off"); }
    else { music.play(); localStorage.setItem("guardia_music_on", "on"); }
  };

  const saveSession = async () => {
    try {
      await api.post("/blueprint/sessions", {
        rounds_completed: blocks.filter((b) => b.kind === "round").length,
        total_minutes: workout.minutes,
        rpe: Math.min(10, 4 + (workout.level || 2)),
        strikes: 0,
        notes: `${workout.title[lang]} (${LEVEL_LABEL[lang][workout.level]})`,
        combos: [],
      }, authHeader());
      toast.success(lang === "it" ? "Sessione salvata!" : "Session saved!");
      onExit();
    } catch (e) {
      toast.error("Error");
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-10">
        <CheckCircle2 size={56} className="text-[#10B981] mx-auto" />
        <h2 className="mt-4 font-heading text-3xl font-black uppercase text-white">{lang === "it" ? "Allenamento completato!" : "Workout complete!"}</h2>
        <p className="mt-2 text-zinc-400">{workout.title[lang]} · {workout.minutes} min</p>
        <div className="mt-6 flex gap-3 justify-center">
          <button onClick={saveSession} data-testid="workout-save-btn" className="px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md">{lang === "it" ? "Salva nel log" : "Save to log"}</button>
          <button onClick={onExit} data-testid="workout-exit-btn" className="px-6 py-3 border border-[#272b35] text-zinc-300 rounded-md">{lang === "it" ? "Esci" : "Exit"}</button>
        </div>
      </div>
    );
  }

  const pct = (idx / blocks.length) * 100;

  if (!picked) {
    return <CoachPicker lang={lang} style={style} workout={workout} onExit={onExit}
      onPick={(c) => { setStyle(c); localStorage.setItem("guardia_coach_style", c); setPicked(true); }} />;
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button onClick={onExit} data-testid="workout-back-btn" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white min-w-0">
          <ArrowLeft size={15} className="shrink-0" /> <span className="truncate">{workout.title[lang]}</span>
        </button>
        <button onClick={() => { coach.stop(); coach.setEnabled(!coach.enabled); }} data-testid="coach-voice-toggle"
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase border transition-colors ${coach.enabled ? "border-[#E63946] text-white bg-[#E63946]/15" : "border-[#272b35] text-zinc-500"}`}>
          {coach.enabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {lang === "it" ? "Voce coach" : "Coach voice"}
          {coach.enabled && !coach.ready && <span data-testid="coach-voice-loading" className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />}
        </button>
      </div>

      <MusicBar music={music} onToggle={toggleMusic} lang={lang} />
      <p data-testid="wake-lock-note" className="-mt-2 mb-4 flex items-center gap-1.5 text-[11px] text-zinc-500">
        <MonitorSmartphone size={12} /> {lang === "it" ? "Durante l'allenamento lo schermo resta acceso, così musica e coach non si fermano." : "During the workout the screen stays on, so music and coach never stop."}
        <span className="ml-auto font-mono uppercase text-zinc-400">{COACH_CARDS[style][lang][0]}</span>
      </p>

      <div className="h-1.5 bg-[#14161b] rounded-full overflow-hidden mb-6">
        <div className="h-full bg-[#E63946] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: meta.color, backgroundColor: `${meta.color}0d` }}>
        <span className="text-xs font-mono uppercase tracking-[0.25em]" style={{ color: meta.color }}>{meta.label[lang]} · {idx + 1}/{blocks.length}</span>
        <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold uppercase text-white">{block.name[lang]}</h3>
        <div data-testid="workout-timer" className="font-mono-data font-bold text-white text-6xl sm:text-7xl my-4 tabular-nums">{fmt(remaining)}</div>
        <StepList block={block} seg={seg} segIdx={segIdx} elapsed={elapsed} lang={lang} color={meta.color} />
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <button onClick={() => goTo(idx - 1, false)} data-testid="workout-prev-btn" disabled={idx === 0} className="p-3 border border-[#272b35] text-white rounded-md disabled:opacity-40"><SkipBack size={18} /></button>
        {!running ? (
          <button onClick={start} data-testid="workout-play-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md"><Play size={18} /> {lang === "it" ? "Avvia" : "Start"}</button>
        ) : (
          <button onClick={start} data-testid="workout-pause-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-[#F59E0B] text-black font-bold uppercase rounded-md"><Pause size={18} /> {lang === "it" ? "Pausa" : "Pause"}</button>
        )}
        <button onClick={() => { bell(); goTo(idx + 1, running); }} data-testid="workout-next-btn" className="p-3 border border-[#272b35] text-white rounded-md"><SkipForward size={18} /></button>
      </div>

      {idx + 1 < blocks.length && (
        <p className="mt-5 text-center text-xs font-mono text-zinc-500">{lang === "it" ? "Blocco successivo" : "Next block"}: <span className="text-zinc-300">{blocks[idx + 1].name[lang]}</span></p>
      )}
    </div>
  );
}

function useWakeLock(active) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let lock = null;
    let alive = true;
    const req = () => navigator.wakeLock.request("screen").then((l) => { if (alive) lock = l; else l.release(); }).catch(() => {});
    const onVis = () => { if (document.visibilityState === "visible") req(); };
    req();
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVis); lock?.release().catch(() => {}); };
  }, [active]);
}

const COACH_CARDS = {
  gritty: { icon: Flame, it: ["Coach Grintoso", "Vecchia scuola: rauco, urla dall'angolo, ti sprona e ti prende in giro."], en: ["Gritty Coach", "Old school: raspy, barks from the corner, pushes and teases you."] },
  calm: { icon: Target, it: ["Coach Tecnico", "Calmo e preciso: ti guida sulla tecnica, respiro e posizione."], en: ["Technical Coach", "Calm and precise: guides your technique, breathing and stance."] },
};

function CoachPicker({ lang, style, workout, onPick, onExit }) {
  const stored = localStorage.getItem("guardia_coach_style");
  return (
    <div data-testid="coach-picker" className="max-w-2xl mx-auto">
      <button onClick={onExit} data-testid="coach-picker-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={15} /> {workout.title[lang]}
      </button>
      <h2 className="font-heading text-3xl font-black uppercase text-white">{lang === "it" ? "Scegli il tuo coach" : "Choose your coach"}</h2>
      <p className="mt-1 text-sm text-zinc-400">{lang === "it" ? "Chi ti accompagna in questo allenamento?" : "Who's in your corner for this workout?"}</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {Object.entries(COACH_CARDS).map(([id, c]) => (
          <button key={id} onClick={() => onPick(id)} data-testid={`coach-pick-${id}`}
            className={`flex flex-col items-start text-left rounded-xl border p-5 transition-colors hover:border-[#E63946] ${style === id ? "border-[#E63946] bg-[#E63946]/10" : "border-[#272b35] bg-[#14161b]"}`}>
            <c.icon size={22} className="text-[#E63946]" />
            <p className="mt-3 font-heading text-2xl font-bold uppercase text-white">{c[lang][0]}</p>
            <p className="mt-1 text-sm text-zinc-400">{c[lang][1]}</p>
            {stored === id && <span className="mt-3 inline-block text-[10px] font-mono uppercase tracking-widest text-[#E63946]">{lang === "it" ? "Ultima scelta" : "Last choice"}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepList({ block, seg, segIdx, elapsed, lang, color }) {
  const n = block.cues.length;
  const active = segIdx % n;
  const loop = Math.floor(segIdx / n) + 1;
  const stepLeft = Math.max(0, Math.ceil(seg.segLen * (segIdx + 1) - elapsed));
  const it = lang === "it";
  const mode = block.kind === "rest" ? (it ? "Recupero attivo: segui le indicazioni fino alla campana" : "Active rest: follow the cues until the bell")
    : seg.circuit ? (it ? `Circuito: esegui gli step in ordine e ripeti la sequenza${seg.loops > 1 ? ` ×${seg.loops}` : ""} fino alla campana` : `Circuit: do the steps in order and repeat the sequence${seg.loops > 1 ? ` ×${seg.loops}` : ""} until the bell`)
    : (it ? "Sequenza: un esercizio dopo l'altro, il coach ti dice quando cambiare" : "Sequence: one exercise after the other, the coach tells you when to switch");
  return (
    <div data-testid="workout-steps">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p data-testid="workout-mode" className="text-xs text-zinc-400">{mode}</p>
        {seg.loops > 1 && <span data-testid="workout-loop" className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border border-[#272b35] text-zinc-300">{it ? "Giro" : "Lap"} {loop}/{seg.loops}</span>}
      </div>
      <ol className="space-y-2">
        {block.cues.map((c, i) => {
          const isNow = block.kind !== "rest" && i === active;
          const isNext = block.kind !== "rest" && n > 1 && i === (active + 1) % n;
          return (
            <li key={i} data-testid={`workout-step-${i}`}
              className={`relative flex items-start gap-3 rounded-lg px-3 py-2.5 border transition-colors ${isNow ? "bg-white/5" : "border-transparent"}`}
              style={isNow ? { borderColor: color } : undefined}>
              <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full text-[11px] font-bold font-mono"
                style={{ backgroundColor: isNow ? color : "#272b35", color: "#fff" }}>{i + 1}</span>
              <span className={`flex-1 text-sm leading-snug ${isNow ? "text-white font-semibold" : "text-zinc-400"}`}>{c[lang]}</span>
              {isNow && n > 1 && <span className="shrink-0 text-[10px] font-mono uppercase font-bold" style={{ color }}>{it ? "Ora" : "Now"} · {stepLeft}s</span>}
              {isNext && <span className="shrink-0 text-[10px] font-mono uppercase text-zinc-500">{it ? "Poi" : "Next"}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function MusicBar({ music, onToggle, lang }) {
  return (
    <div data-testid="music-bar" className="mb-4 flex items-center gap-3 rounded-lg border border-[#272b35] bg-[#14161b] px-3 py-2">
      <button onClick={onToggle} data-testid="music-toggle" className={`shrink-0 p-1.5 rounded-full ${music.playing ? "bg-[#E63946] text-white" : "bg-[#272b35] text-zinc-400"}`}>
        <Music size={14} />
      </button>
      <select value={music.track} onChange={(e) => music.setTrack(e.target.value)} data-testid="music-track-select"
        className="flex-1 min-w-0 bg-transparent text-xs text-zinc-200 outline-none">
        {TRACKS.map((t) => <option key={t.id} value={t.id} className="bg-[#14161b]">{t.name}</option>)}
      </select>
      <input type="range" min={0} max={1} step={0.05} value={music.volume} onChange={(e) => music.setVolume(Number(e.target.value))}
        data-testid="music-volume" aria-label={lang === "it" ? "Volume musica" : "Music volume"} className="w-20 sm:w-28 accent-[#E63946]" />
    </div>
  );
}
